import React from "react";
import { InlineMath } from "react-katex";
import {
  createNumericInputTask,
  DigitRow,
  StepStrip,
  WrittenBoard,
  padDigits,
  randInt,
} from "../sharedWrittenNatural";

function pow10(exp) {
  return 10 ** exp;
}

function randNDigit(digits) {
  return randInt(pow10(digits - 1), pow10(digits) - 1);
}

function hasCarry(a, b) {
  let x = a;
  let y = b;
  let carry = 0;
  while (x > 0 || y > 0) {
    const sum = (x % 10) + (y % 10) + carry;
    if (sum >= 10) return true;
    carry = sum >= 10 ? 1 : 0;
    x = Math.floor(x / 10);
    y = Math.floor(y / 10);
  }
  return false;
}

function pickByDigits(aDigits, bDigits) {
  for (let i = 0; i < 500; i += 1) {
    let a = randNDigit(aDigits);
    let b = randNDigit(bDigits);
    if (b > a) [a, b] = [b, a];
    if (hasCarry(a, b)) return { a, b, result: a + b };
  }
  const fallbackA = randNDigit(aDigits);
  const fallbackB = randNDigit(bDigits);
  const a = Math.max(fallbackA, fallbackB);
  const b = Math.min(fallbackA, fallbackB);
  return { a, b, result: a + b };
}

export function pickAdditionTwoByTwo() {
  return pickByDigits(2, 2);
}

export function pickAdditionThreeByTwo() {
  return pickByDigits(3, 2);
}

export function pickAdditionThreeByThree() {
  return pickByDigits(3, 3);
}

export function pickAdditionFourByFour() {
  return pickByDigits(4, 4);
}

export function pickAdditionFiveByThree() {
  return pickByDigits(5, 3);
}

export function pickAdditionFiveByFour() {
  return pickByDigits(5, 4);
}

export function pickAdditionFiveByFive() {
  return pickByDigits(5, 5);
}

export function pickAdditionSixByFour() {
  return pickByDigits(6, 4);
}

export function pickAdditionLargeMixed() {
  const options = [
    [5, 3],
    [5, 4],
    [5, 5],
    [6, 4],
  ];
  const [aDigits, bDigits] = options[randInt(0, options.length - 1)];
  return pickByDigits(aDigits, bDigits);
}

function makePicker(aDigits, bDigits) {
  return () => pickByDigits(aDigits, bDigits);
}

export function analyzeWrittenAddition(a, b) {
  const result = a + b;
  const operandCols = Math.max(String(a).length, String(b).length);
  const hasFinalCarry = String(result).length > operandCols;
  const cols = hasFinalCarry ? operandCols + 1 : operandCols;
  const aDigits = padDigits(a, cols);
  const bDigits = padDigits(b, cols);
  const resultDigits = Array(cols).fill(" ");
  const carryDigits = Array(cols).fill(" ");
  const detailLines = [];
  const steps = [];
  let carry = 0;

  const firstOperandColumn = hasFinalCarry ? 1 : 0;

  for (let c = cols - 1; c >= firstOperandColumn; c -= 1) {
    const da = Number(aDigits[c] === " " ? 0 : aDigits[c]);
    const db = Number(bDigits[c] === " " ? 0 : bDigits[c]);
    const sum = da + db + carry;
    const isLastColumnWithFinalCarry = hasFinalCarry && c === firstOperandColumn;
    const digit = sum % 10;
    const carryOut = Math.floor(sum / 10);
    const carryTarget = c - 1;
    const showCarry = carryOut > 0 && carryTarget >= 0 && !isLastColumnWithFinalCarry;
    const highlightResult = isLastColumnWithFinalCarry && carryOut > 0 ? [carryTarget, c] : [c];
    const writtenValue = isLastColumnWithFinalCarry && carryOut > 0 ? `${carryOut}${digit}` : `${digit}`;
    if (isLastColumnWithFinalCarry && carryOut > 0) {
      resultDigits[carryTarget] = String(carryOut);
    }
    resultDigits[c] = String(digit);
    if (showCarry) carryDigits[carryTarget] = String(carryOut);

    detailLines.push({
      da,
      db,
      carryIn: carry,
      sum,
      digit,
      carryOut: showCarry ? carryOut : 0,
      writtenValue,
    });
    steps.push({
      aDigits: [...aDigits],
      bDigits: [...bDigits],
      resultDigits: [...resultDigits],
      carryDigits: [...carryDigits],
      highlightResult,
      highlightCarry: showCarry ? [carryTarget] : [],
    });
    carry = carryOut;
  }

  steps.push({
    aDigits: [...aDigits],
    bDigits: [...bDigits],
    resultDigits: [...resultDigits],
    carryDigits: [...carryDigits],
    highlightResult: [],
    highlightCarry: [],
  });

  return { cols, detailLines, steps };
}

export function WrittenAdditionBoard({ step }) {
  return (
    <WrittenBoard>
      <DigitRow digits={step.carryDigits} small highlightIndices={step.highlightCarry} />
      <DigitRow digits={step.aDigits} />
      <DigitRow sign="+" digits={step.bDigits} />
      <div style={{ margin: "2px 0", borderTop: "1px solid var(--color-text)" }} />
      <DigitRow digits={step.resultDigits} highlightIndices={step.highlightResult} />
    </WrittenBoard>
  );
}

function makeAdditionTask(type, pickData) {
  const mod = createNumericInputTask({
    type,
    pickData,
    renderTaskText: (data) => (
      <>
        Oblicz sposobem pisemnym: <InlineMath math={`${data.a}+${data.b}`} />.
      </>
    ),
    explain: (data) => {
      const written = analyzeWrittenAddition(data.a, data.b);
      return (
        <div style={{ textAlign: "left" }}>
          <p>
            <strong>Krok 1:</strong> Ustawiamy liczby pionowo: jedności pod
            jednościami, dziesiątki pod dziesiątkami itd.
          </p>
          <p>
            <strong>Krok 2:</strong> Dodajemy od prawej strony. Jeśli suma w
            kolumnie ma co najmniej 10, zapisujemy cyfrę jedności, a resztę
            przenosimy nad kolejną kolumnę.
          </p>
          <ul>
            {written.detailLines.map((line, idx) => (
              <li key={idx}>
                <InlineMath
                  math={
                    line.carryIn
                      ? `${line.da}+${line.db}+${line.carryIn}=${line.sum}`
                      : `${line.da}+${line.db}=${line.sum}`
                  }
                />{" "}
                {line.carryOut > 0 ? (
                  <>
                    {"\u2192"} zapisujemy <InlineMath math={`${line.writtenValue}`} />, przenosimy{" "}
                    <InlineMath math={`${line.carryOut}`} />
                  </>
                ) : (
                  <>
                    {"\u2192"} zapisujemy <InlineMath math={`${line.writtenValue}`} />
                  </>
                )}
              </li>
            ))}
          </ul>
          <StepStrip
            steps={written.steps.map((step, idx) => (
              <WrittenAdditionBoard key={idx} step={step} />
            ))}
          />
          <p>
            <strong>Wynik:</strong> <InlineMath math={`${data.result}`} />
          </p>
        </div>
      );
    },
  });

  return mod;
}

export const NaturalWrittenAdditionTwoByTwoTask = makeAdditionTask(
  "NaturalWrittenAdditionTwoByTwoTask",
  makePicker(2, 2)
);
export const NaturalWrittenAdditionThreeByTwoTask = makeAdditionTask(
  "NaturalWrittenAdditionThreeByTwoTask",
  makePicker(3, 2)
);
export const NaturalWrittenAdditionThreeByThreeTask = makeAdditionTask(
  "NaturalWrittenAdditionThreeByThreeTask",
  makePicker(3, 3)
);
export const NaturalWrittenAdditionFourByFourTask = makeAdditionTask(
  "NaturalWrittenAdditionFourByFourTask",
  makePicker(4, 4)
);
export const NaturalWrittenAdditionFiveByThreeTask = makeAdditionTask(
  "NaturalWrittenAdditionLargeMixedTask",
  pickAdditionLargeMixed
);
